import { Box } from '@mui/material';
import { motion } from 'framer-motion';
import React, { memo, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { ISegmentedControlOption } from '../../../../types/shell';

import styles from './SegmentedControl.module.scss';

interface IProps<T extends string = string> {
  options: ISegmentedControlOption<T>[];
  value: T;
  onChange: (value: T) => void;
  dataAction?: string;
  fullWidth?: boolean;
  gap?: number;
  dimensions?: {
    containerWidth?: number;
    containerHeight?: number;
    buttonWidth?: number;
    buttonHeight?: number;
  };
}

export const SegmentedControl = memo(
  <T extends string = string>({
    options,
    value,
    onChange,
    dataAction,
    fullWidth = false,
    gap = 4,
    dimensions,
  }: IProps<T>) => {
    const buttonRefs = useRef<(HTMLButtonElement | null)[]>([]);
    const [sliderPosition, setSliderPosition] = useState({ x: 0, width: 0 });

    const defaultButtonWidth = 150;
    const defaultButtonHeight = 30;
    const defaultContainerWidth = 313;
    const defaultContainerHeight = 38;

    const buttonWidth = dimensions?.buttonWidth ?? defaultButtonWidth;
    const buttonHeight = dimensions?.buttonHeight ?? defaultButtonHeight;
    const containerWidth = dimensions?.containerWidth ?? defaultContainerWidth;
    const containerHeight = dimensions?.containerHeight ?? defaultContainerHeight;

    const activeIndex = useMemo(() => {
      return options.findIndex((option) => option.value === value);
    }, [options, value]);

    const calculatedSliderPosition = useMemo(() => {
      if (activeIndex === -1) {
        return { x: 0, width: 0 };
      }

      if (!fullWidth) {
        const x = activeIndex * (buttonWidth + gap);

        return {
          x,
          width: buttonWidth,
        };
      }

      return sliderPosition;
    }, [activeIndex, fullWidth, gap, sliderPosition, buttonWidth]);

    // Measure actual button positions for fullWidth mode
    useLayoutEffect(() => {
      if (fullWidth && activeIndex !== -1 && buttonRefs.current[activeIndex]) {
        const button = buttonRefs.current[activeIndex];
        const container = button?.parentElement;

        if (button && container) {
          const buttonRect = button.getBoundingClientRect();
          const containerRect = container.getBoundingClientRect();
          const x = buttonRect.left - containerRect.left;
          const width = buttonRect.width;

          setSliderPosition({ x, width });
        }
      }
    }, [activeIndex, fullWidth]);

    const containerClass = `${styles.segmentedControl} ${fullWidth ? styles.fullWidth : ''}`;
    const buttonClass = (option: ISegmentedControlOption<T>) =>
      `${styles.segmentedControlButton} ${fullWidth ? styles.fullWidthButton : ''} ${
        value === option.value ? styles.active : ''
      }`;

    // Apply custom dimensions
    const containerStyle = {
      width: fullWidth ? '100%' : `${containerWidth}px`,
      height: `${containerHeight}px`,
      gap: fullWidth ? `${gap}px` : undefined,
    } as const;

    const buttonStyle = fullWidth
      ? {}
      : {
          width: `${buttonWidth}px`,
          height: `${buttonHeight}px`,
          flex: `0 0 ${buttonWidth}px`,
        };

    const sliderStyle: React.CSSProperties = {
      height: `${buttonHeight}px`,
    };

    return (
      <Box className={containerClass} data-action={dataAction} sx={containerStyle} role="tablist">
        <Box className={styles.segmentedControlButtons} sx={{ gap: `${gap}px` }}>
          <motion.div
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
            className={styles.segmentedControlSlider}
            initial={false}
            animate={calculatedSliderPosition}
            style={sliderStyle}
          />
          {options.map((option, index) => (
            <button
              key={option.value}
              ref={(el) => {
                buttonRefs.current[index] = el;
              }}
              role="tab"
              aria-selected={value === option.value}
              className={buttonClass(option)}
              onClick={() => onChange(option.value)}
              data-action={option.dataAction || `${dataAction}_${option.value}_btn`}
              style={buttonStyle}
            >
              {option.icon && <span className={styles.segmentedControlIcon}>{option.icon}</span>}
              {option.label}
            </button>
          ))}
        </Box>
      </Box>
    );
  },
) as <T extends string = string>(props: IProps<T>) => JSX.Element;
